import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/games.css';

// ============================================
// VIDEO CONFIG - PASTE YOUR VIDEO URLS HERE
// Each entry: { name, target, video }
// video can be:
//   - YouTube Shorts URL: "https://youtube.com/shorts/VIDEO_ID"
//   - Direct MP4 URL: "https://example.com/video.mp4"
//   - null for placeholder
// ============================================
const MOVES_CONFIG = [
  { name: "Handstand Hold", target: "20 sec x 5", subtitle: "wall handstands count!", video: "https://youtube.com/shorts/ofBjDw1magI" },
  { name: "Cartwheels", target: "5 each side x 5", subtitle: null, video: "https://youtube.com/shorts/Hafj7qM-cS8" },
  { name: "Splits Practice", target: "30 sec each leg x 5", subtitle: null, video: "https://youtube.com/shorts/uqyD8yNhO9I" },
  { name: "Squat Jumps", target: "10 x 5", subtitle: null, video: "https://youtube.com/shorts/wBIQgkFsonY" },
  { name: "Frog Splats", target: "8 x 5", subtitle: "burpees!", video: "https://youtube.com/shorts/krYB-Br16m4" },
  { name: "Plank Hold", target: "30 sec x 5", subtitle: null, video: "https://youtube.com/shorts/AC_f8l1eowY" },
  { name: "Jumping Jacks", target: "25 x 5", subtitle: null, video: "https://youtube.com/shorts/30wrgxqCzgg" },
  { name: "Cheetah Sprint", target: "5 big sprints", subtitle: "10+ sec each", video: "https://youtube.com/shorts/Mv1PaZSqezA" },
  { name: "Bridge / Backbend", target: "hold 10 sec x 5", subtitle: null, video: "https://youtube.com/shorts/tIxc_0OyzCM" },
  { name: "Bear Crawl", target: "across the room x 5", subtitle: null, video: "https://youtube.com/shorts/7QNOf4tIBJk" },
  { name: "Crab Walk", target: "across the room x 5", subtitle: null, video: "https://youtube.com/shorts/K-sJ_EGgOZ0" },
  { name: "Straddle Stretch", target: "1 min x 5", subtitle: null, video: "https://youtube.com/shorts/HUK2eU_wRCM" },
  { name: "FREE", target: "You showed up.", subtitle: "That counts!", video: null, isFree: true },
  { name: "Wall Sit", target: "30 sec x 5", subtitle: null, video: "https://youtube.com/shorts/EldBZisnF5s" },
  { name: "Flamingo March", target: "30 sec x 5", subtitle: "high knees", video: "https://youtube.com/shorts/1-ob483QtOo" },
  { name: "Frog Jumps", target: "10 x 5", subtitle: null, video: "https://youtube.com/shorts/grUY092aHGE" },
  { name: "Kangaroo Hops", target: "10 big hops x 5", subtitle: null, video: "https://youtube.com/shorts/KikAZ3SYr5k" },
  { name: "Superman Hold", target: "20 sec x 5", subtitle: null, video: "https://youtube.com/shorts/4xS5-4Qelv4" },
  { name: "Banana Boat", target: "20 sec x 5", subtitle: "hollow hold", video: "https://youtube.com/shorts/hQ0yidx-2H8" },
  { name: "Duck Walk", target: "across the room x 5", subtitle: null, video: "https://youtube.com/shorts/RtMXu1c0Fo8" },
  { name: "Inchworm Walk", target: "6 walks x 5", subtitle: null, video: "https://youtube.com/shorts/oDKG--OwEj8" },
  { name: "Alligator Drag", target: "across the room x 5", subtitle: null, video: "https://youtube.com/shorts/nOqMkKS9ObM" },
  { name: "One-Foot Balance", target: "20 sec each x 5", subtitle: "eyes closed!", video: "https://youtube.com/shorts/vMFwmwIjWhk" },
  { name: "Forward Rolls", target: "3 x 5", subtitle: null, video: "https://youtube.com/shorts/-k1bby5yFWE" },
  { name: "Dance Party", target: "1 whole song x 5", subtitle: null, video: "https://youtube.com/shorts/ljaL-8eEVXo" },
];

const STORAGE_KEY = 'gcf-bingo-progress';
const DAILY_KEY = 'gcf-bingo-daily';
const COMPLETE_KEY = 'gcf-bingo-complete-shown';
const MAX_DAILY_CHECKS = 3;
const TOTAL_CHECKS = 72; // 24 moves x 3 days each

const GamesPage = () => {
  const [progress, setProgress] = useState({});
  const [dailyChecks, setDailyChecks] = useState({ date: '', count: 0 });
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [showDailyLimit, setShowDailyLimit] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [pdfEmail, setPdfEmail] = useState('');
  const [pdfSubmitting, setPdfSubmitting] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);

  // Load progress from localStorage
  useEffect(() => {
    document.title = "GoCalFit Games";
    
    const savedProgress = localStorage.getItem(STORAGE_KEY);
    if (savedProgress) {
      setProgress(JSON.parse(savedProgress));
    }

    const savedDaily = localStorage.getItem(DAILY_KEY);
    if (savedDaily) {
      const parsed = JSON.parse(savedDaily);
      const today = new Date().toDateString();
      if (parsed.date === today) {
        setDailyChecks(parsed);
      } else {
        setDailyChecks({ date: today, count: 0 });
      }
    } else {
      setDailyChecks({ date: new Date().toDateString(), count: 0 });
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    if (Object.keys(progress).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    }
  }, [progress]);

  // Save daily checks to localStorage
  useEffect(() => {
    if (dailyChecks.date) {
      localStorage.setItem(DAILY_KEY, JSON.stringify(dailyChecks));
    }
  }, [dailyChecks]);

  // Calculate total checks
  const totalChecked = Object.values(progress).reduce((sum, days) => {
    return sum + (days?.filter(Boolean).length || 0);
  }, 0);

  // Check if card is complete and show celebration
  useEffect(() => {
    if (totalChecked === TOTAL_CHECKS) {
      const alreadyShown = localStorage.getItem(COMPLETE_KEY);
      if (!alreadyShown) {
        triggerCelebration();
        localStorage.setItem(COMPLETE_KEY, 'true');
      }
    }
  }, [totalChecked]);

  const triggerCelebration = () => {
    const pieces = [];
    for (let i = 0; i < 100; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        color: ['#FF6B35', '#1A1A1A', '#FFD700', '#4CAF50', '#E91E63'][Math.floor(Math.random() * 5)]
      });
    }
    setConfettiPieces(pieces);
    setShowCelebration(true);
  };

  // Handle PDF email submission
  const handlePdfEmailSubmit = async (e) => {
    e.preventDefault();
    if (!pdfEmail || pdfSubmitting) return;
    
    setPdfSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pdfEmail,
          first_name: 'Bingo',
          last_name: 'Download',
          phone: '',
          source: 'bingo_pdf_download'
        })
      });
      
      if (response.ok) {
        setPdfSuccess(true);
        // Trigger download via backend proxy for correct filename
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/bingo-board`;
      }
    } catch (err) {
      // Still allow download even if API fails
      setPdfSuccess(true);
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/bingo-board`;
    }
    
    setPdfSubmitting(false);
  };

  const handleSquareClick = (index) => {
    if (MOVES_CONFIG[index].isFree) return;
    setSelectedSquare(index);
  };

  const handleCheckDay = (moveIndex, dayIndex) => {
    const today = new Date().toDateString();
    
    // Reset daily count if it's a new day
    let currentDaily = dailyChecks;
    if (currentDaily.date !== today) {
      currentDaily = { date: today, count: 0 };
    }

    const moveKey = `move-${moveIndex}`;
    const currentProgress = progress[moveKey] || [false, false, false];
    
    // If already checked, allow unchecking (no daily limit for unchecking)
    if (currentProgress[dayIndex]) {
      const newProgress = [...currentProgress];
      newProgress[dayIndex] = false;
      setProgress(prev => ({ ...prev, [moveKey]: newProgress }));
      return;
    }

    // Check daily limit for new checks
    if (currentDaily.count >= MAX_DAILY_CHECKS) {
      setShowDailyLimit(true);
      setTimeout(() => setShowDailyLimit(false), 3000);
      return;
    }

    // Make the check
    const newProgress = [...currentProgress];
    newProgress[dayIndex] = true;
    setProgress(prev => ({ ...prev, [moveKey]: newProgress }));
    setDailyChecks({ date: today, count: currentDaily.count + 1 });
  };

  const getSquareChecks = (index) => {
    if (MOVES_CONFIG[index].isFree) return [true, true, true];
    const moveKey = `move-${index}`;
    return progress[moveKey] || [false, false, false];
  };

  const isSquareComplete = (index) => {
    const checks = getSquareChecks(index);
    return checks.every(Boolean);
  };

  const getVideoEmbed = (videoUrl) => {
    if (!videoUrl) return null;
    
    // Check if it's a YouTube Shorts URL
    const shortsMatch = videoUrl.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shortsMatch) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${shortsMatch[1]}`
      };
    }
    
    // Check if it's a regular YouTube URL
    const ytMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) {
      return {
        type: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`
      };
    }
    
    // Assume it's a direct video URL
    return {
      type: 'direct',
      embedUrl: videoUrl
    };
  };

  const renderModal = () => {
    if (selectedSquare === null) return null;
    
    const move = MOVES_CONFIG[selectedSquare];
    const checks = getSquareChecks(selectedSquare);
    const videoInfo = getVideoEmbed(move.video);

    return (
      <div className="bingo-modal-overlay" onClick={() => setSelectedSquare(null)}>
        <div className="bingo-modal" onClick={e => e.stopPropagation()}>
          <button className="bingo-modal-close" onClick={() => setSelectedSquare(null)}>
            <X size={24} />
          </button>
          
          <div className="bingo-modal-content">
            <h2 className="bingo-modal-title">{move.name}</h2>
            <p className="bingo-modal-target">
              {move.target}
              {move.subtitle && <span className="bingo-modal-subtitle"> ({move.subtitle})</span>}
            </p>

            <div className="bingo-modal-video">
              {videoInfo ? (
                videoInfo.type === 'youtube' ? (
                  <iframe
                    src={videoInfo.embedUrl}
                    title={move.name}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video controls playsInline>
                    <source src={videoInfo.embedUrl} type="video/mp4" />
                  </video>
                )
              ) : (
                <div className="bingo-video-placeholder">
                  <p>Coach Cal's demo coming soon —</p>
                  <p>go do the move anyway!</p>
                </div>
              )}
            </div>

            <div className="bingo-modal-checks">
              {['Day 1', 'Day 2', 'Day 3'].map((label, idx) => (
                <button
                  key={idx}
                  className={`bingo-day-check ${checks[idx] ? 'checked' : ''}`}
                  onClick={() => handleCheckDay(selectedSquare, idx)}
                >
                  <span className="bingo-day-circle">
                    {checks[idx] && <Check size={20} />}
                  </span>
                  <span className="bingo-day-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="games-page">
      {/* Daily Limit Toast */}
      {showDailyLimit && (
        <div className="bingo-toast">
          That's 3 today — you're done! Come back tomorrow. 💪
        </div>
      )}

      {/* Celebration Modal */}
      {showCelebration && (
        <div className="bingo-celebration-overlay" onClick={() => setShowCelebration(false)}>
          <div className="bingo-confetti">
            {confettiPieces.map(piece => (
              <div
                key={piece.id}
                className="confetti-piece"
                style={{
                  left: `${piece.left}%`,
                  animationDelay: `${piece.delay}s`,
                  animationDuration: `${piece.duration}s`,
                  backgroundColor: piece.color
                }}
              />
            ))}
          </div>
          <div className="bingo-celebration-content">
            <h2>🎉 CARD COMPLETE! 🎉</h2>
            <p>Bring your fridge card to Fall Open House to claim your NeeDoh!</p>
            <button onClick={() => setShowCelebration(false)}>Awesome!</button>
          </div>
        </div>
      )}

      <div className="bingo-container">
        {/* Header */}
        <header className="bingo-header">
          <h1 className="bingo-title">
            <span className="bingo-orange">BINGO</span>
            <span className="bingo-dark">BORED</span>
          </h1>
          <p className="bingo-subtitle">GoCalFit Summer Challenge — Serious movement. Serious play.</p>
          <p className="bingo-rules">
            Do each move on 3 different days. Max 3 check-offs a day. Grown-up confirms. 
            Fill the whole card, win a NeeDoh at Fall Open House.
          </p>
        </header>

        {/* Progress Bar */}
        <div className="bingo-progress">
          <div className="bingo-progress-text">
            <span className="bingo-progress-count">{totalChecked}</span> of {TOTAL_CHECKS} boxes checked
          </div>
          <div className="bingo-progress-bar">
            <div 
              className="bingo-progress-fill" 
              style={{ width: `${(totalChecked / TOTAL_CHECKS) * 100}%` }}
            />
          </div>
        </div>

        {/* Bingo Grid */}
        <div className="bingo-grid">
          {MOVES_CONFIG.map((move, index) => {
            const checks = getSquareChecks(index);
            const complete = isSquareComplete(index);
            const checkedCount = checks.filter(Boolean).length;
            
            return (
              <button
                key={index}
                className={`bingo-square ${move.isFree ? 'free' : ''} ${complete ? 'complete' : ''}`}
                onClick={() => handleSquareClick(index)}
                disabled={move.isFree}
              >
                {complete && !move.isFree && (
                  <div className="bingo-square-check">
                    <Check size={24} />
                  </div>
                )}
                <span className="bingo-square-name">{move.name}</span>
                <span className="bingo-square-target">{move.target}</span>
                {!move.isFree && (
                  <div className="bingo-square-dots">
                    {[0, 1, 2].map(i => (
                      <span key={i} className={`bingo-dot ${checks[i] ? 'filled' : ''}`} />
                    ))}
                  </div>
                )}
                {move.isFree && (
                  <span className="bingo-free-sub">{move.subtitle}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <footer className="bingo-footer">
          <p>The paper card on your fridge is your golden ticket — bring it to Fall Open House to claim your prize.</p>
          
          {/* PDF Email Capture */}
          <div className="bingo-pdf-section">
            {!pdfSuccess ? (
              <form onSubmit={handlePdfEmailSubmit} className="bingo-pdf-form">
                <label className="bingo-pdf-label">Want the printable card? Drop your email →</label>
                <div className="bingo-pdf-input-row">
                  <input
                    type="email"
                    value={pdfEmail}
                    onChange={(e) => setPdfEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bingo-pdf-input"
                  />
                  <button type="submit" disabled={pdfSubmitting} className="bingo-pdf-btn">
                    {pdfSubmitting ? '...' : 'Get the Card'}
                  </button>
                </div>
              </form>
            ) : (
              <p className="bingo-pdf-success">✅ Downloading now! Check your downloads folder.</p>
            )}
          </div>
          
          <a href="/" className="bingo-logo-link">
            <span className="bingo-footer-logo-text">GoCalFit</span>
          </a>
          
          {/* CTA for non-members */}
          <div className="bingo-cta-line">
            <span>Not a GoCalFit family yet? </span>
            <Link to="/aerial" className="bingo-cta-link">Come fly with us →</Link>
          </div>
        </footer>
      </div>

      {/* Modal */}
      {renderModal()}
    </div>
  );
};

export default GamesPage;
